import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAdminPlayers, getAdminPlayer, updatePlayerStatus, updatePlayerInfo, deletePlayer, Player } from '../api/admin.api';
import LoadingSpinner from '../components/LoadingSpinner';
import DeletePlayerModal from '../components/DeletePlayerModal';
import upazilas from '../data/bd-upazilas.json';

const divisions = [
  'Barisal',
  'Chattogram',
  'Dhaka',
  'Khulna',
  'Mymensingh',
  'Rajshahi',
  'Rangpur',
  'Sylhet',
];

const districts = [
  { name: 'Bagerhat', division: 'Khulna' },
  { name: 'Bandarban', division: 'Chattogram' },
  { name: 'Barguna', division: 'Barisal' },
  { name: 'Barisal', division: 'Barisal' },
  { name: 'Bhola', division: 'Barisal' },
  { name: 'Bogra', division: 'Rajshahi' },
  { name: 'Brahmanbaria', division: 'Chattogram' },
  { name: 'Chandpur', division: 'Chattogram' },
  { name: 'Chapai Nawabganj', division: 'Rajshahi' },
  { name: 'Chattogram', division: 'Chattogram' },
  { name: 'Chuadanga', division: 'Khulna' },
  { name: 'Comilla', division: 'Chattogram' },
  { name: "Cox's Bazar", division: 'Chattogram' },
  { name: 'Dhaka', division: 'Dhaka' },
  { name: 'Dinajpur', division: 'Rangpur' },
  { name: 'Faridpur', division: 'Dhaka' },
  { name: 'Feni', division: 'Chattogram' },
  { name: 'Gaibandha', division: 'Rangpur' },
  { name: 'Gazipur', division: 'Dhaka' },
  { name: 'Gopalganj', division: 'Dhaka' },
  { name: 'Habiganj', division: 'Sylhet' },
  { name: 'Jamalpur', division: 'Mymensingh' },
  { name: 'Jashore', division: 'Khulna' },
  { name: 'Jhalokati', division: 'Barisal' },
  { name: 'Jhenaidah', division: 'Khulna' },
  { name: 'Joypurhat', division: 'Rajshahi' },
  { name: 'Khagrachari', division: 'Chattogram' },
  { name: 'Khulna', division: 'Khulna' },
  { name: 'Kishoreganj', division: 'Dhaka' },
  { name: 'Kurigram', division: 'Rangpur' },
  { name: 'Kushtia', division: 'Khulna' },
  { name: 'Lakshmipur', division: 'Chattogram' },
  { name: 'Lalmonirhat', division: 'Rangpur' },
  { name: 'Madaripur', division: 'Dhaka' },
  { name: 'Magura', division: 'Khulna' },
  { name: 'Manikganj', division: 'Dhaka' },
  { name: 'Meherpur', division: 'Khulna' },
  { name: 'Moulvibazar', division: 'Sylhet' },
  { name: 'Munshiganj', division: 'Dhaka' },
  { name: 'Mymensingh', division: 'Mymensingh' },
  { name: 'Naogaon', division: 'Rajshahi' },
  { name: 'Narail', division: 'Khulna' },
  { name: 'Narayanganj', division: 'Dhaka' },
  { name: 'Narsingdi', division: 'Dhaka' },
  { name: 'Natore', division: 'Rajshahi' },
  { name: 'Netrokona', division: 'Mymensingh' },
  { name: 'Nilphamari', division: 'Rangpur' },
  { name: 'Noakhali', division: 'Chattogram' },
  { name: 'Pabna', division: 'Rajshahi' },
  { name: 'Panchagarh', division: 'Rangpur' },
  { name: 'Patuakhali', division: 'Barisal' },
  { name: 'Pirojpur', division: 'Barisal' },
  { name: 'Rajbari', division: 'Dhaka' },
  { name: 'Rajshahi', division: 'Rajshahi' },
  { name: 'Rangamati', division: 'Chattogram' },
  { name: 'Rangpur', division: 'Rangpur' },
  { name: 'Satkhira', division: 'Khulna' },
  { name: 'Shariatpur', division: 'Dhaka' },
  { name: 'Sherpur', division: 'Mymensingh' },
  { name: 'Sirajganj', division: 'Rajshahi' },
  { name: 'Sunamganj', division: 'Sylhet' },
  { name: 'Sylhet', division: 'Sylhet' },
  { name: 'Tangail', division: 'Dhaka' },
  { name: 'Thakurgaon', division: 'Rangpur' },
];

const roles = ['Batsman', 'Bowler', 'All-Rounder', 'Wicket-Keeper'];
const battingStyles = ['Right Hand', 'Left Hand'];
const bowlingStyles = [
  'Right Arm Fast',
  'Right Arm Medium',
  'Left Arm Fast',
  'Left Arm Spin',
  'Right Arm Spin',
];
const genderOptions = ['Male', 'Female', 'Other'];

type UpazilaEntry = {
  upazila: string;
  district: string;
  division: string;
};

const isEmail = (value: string) => /.+@.+\..+/.test(value);

const AdminPlayersPage: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    gender: '',
    father_name: '',
    mother_name: '',
    phone: '',
    whatsapp_number: '',
    email: '',
    dob: '',
    division: '',
    district: '',
    upazilla: '',
    playing_role: '',
    batting_style: '',
    bowling_style: '',
    jersey_size: '',
    nid: '',
    cricheroes_profile_link: '',
  });
  const [editProfilePhoto, setEditProfilePhoto] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [deleteModal, setDeleteModal] = useState<{show: boolean, playerId: string | null, playerName: string}>({
    show: false,
    playerId: null,
    playerName: ''
  });
  
  const upazilaData = upazilas as UpazilaEntry[];

  const requiresBowlingStyle = useMemo(
    () => ['Bowler', 'All-Rounder'].includes(editFormData.playing_role),
    [editFormData.playing_role]
  );

  const availableDistricts = useMemo(() => {
    if (!editFormData.division) {
      return [];
    }
    return districts.filter((district) => district.division === editFormData.division);
  }, [editFormData.division]);

  const availableUpazilas = useMemo(() => {
    if (!editFormData.district) {
      return [];
    }
    const selectedDistrict = districts.find(d => d.name === editFormData.district);
    if (!selectedDistrict) {
      return [];
    }
    return upazilaData.filter((upazila) => upazila.district === selectedDistrict.name);
  }, [editFormData.district, upazilaData]);

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilters.length > 0) {
        filters.status = statusFilters;
      }
      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }
      const response = await getAdminPlayers(filters);
      setPlayers(response.data || []);
    } catch (err) {
      // Silent fail for prototype
    } finally {
      setLoading(false);
    }
  }, [statusFilters, searchQuery]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const handleSearch = () => {
    fetchPlayers();
  };

  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => (prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status]));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilters([]);
  };

  const isClearDisabled = searchQuery.trim().length === 0 && statusFilters.length === 0;

  const handleViewPlayer = async (playerId: string) => {
    try {
      const response = await getAdminPlayer(playerId);
      setSelectedPlayer(response.data);
    } catch (err) {
      // Silent fail for prototype
    }
  };

  const handleStatusChange = async (playerId: string, newStatus: 'APPROVED' | 'DECLINED' | 'PENDING_APPROVAL') => {
    try {
      await updatePlayerStatus(playerId, { status: newStatus });
      setPlayers(prev => prev.map(p => 
        p.id === playerId ? { ...p, status: newStatus } : p
      ));
      if (selectedPlayer?.id === playerId) {
        setSelectedPlayer(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err) {
      // Silent fail for prototype
    }
  };

  const handleDeletePlayer = (playerId: string, playerName: string) => {
    setDeleteModal({
      show: true,
      playerId,
      playerName
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.playerId) return;

    try {
      await deletePlayer(deleteModal.playerId);
      // Remove player from list
      setPlayers(prev => prev.filter(p => p.id !== deleteModal.playerId));
      // Clear selected player if it was the deleted one
      if (selectedPlayer?.id === deleteModal.playerId) {
        setSelectedPlayer(null);
      }
      // Close modal
      setDeleteModal({ show: false, playerId: null, playerName: '' });
    } catch (err: any) {
      console.error('Delete player error:', err);
      let errorMessage = 'Failed to delete player. Please try again.';
      
      if (err.response?.status === 404) {
        errorMessage = 'Player not found. It may have already been deleted.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to delete players.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Please log in again to continue.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      alert(errorMessage);
      // Keep modal open on error so user can try again
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, playerId: null, playerName: '' });
  };

  const handleOpenEditModal = () => {
    if (selectedPlayer) {
      // Format date of birth
      let dobValue = '';
      if (selectedPlayer.dob) {
        if (selectedPlayer.dob.includes('T')) {
          dobValue = selectedPlayer.dob.split('T')[0];
        } else {
          dobValue = selectedPlayer.dob;
        }
      }
      
      // Get division and district values - keep them as-is
      // The dropdown will handle showing the value even if it's not in the filtered list
      setEditFormData({
        full_name: selectedPlayer.name || '',
        gender: (selectedPlayer as any).gender || '',
        father_name: (selectedPlayer as any).father_name || '',
        mother_name: (selectedPlayer as any).mother_name || '',
        phone: selectedPlayer.phone || '',
        whatsapp_number: (selectedPlayer as any).whatsapp_number || selectedPlayer.phone || '',
        email: selectedPlayer.email || '',
        dob: dobValue,
        division: selectedPlayer.division || '',
        district: selectedPlayer.district_name || selectedPlayer.district_code || '',
        upazilla: selectedPlayer.upazilla || '',
        playing_role: selectedPlayer.playing_role || '',
        batting_style: selectedPlayer.batting_style || '',
        bowling_style: selectedPlayer.bowling_style || '',
        jersey_size: selectedPlayer.jersey_size || '',
        nid: (selectedPlayer as any).nid || '',
        cricheroes_profile_link: selectedPlayer.cricheroes_profile_link || '',
      });
      setEditProfilePhoto(null);
      // Set photo preview if player has profile photo URL
      if ((selectedPlayer as any).profile_photo) {
        setEditPhotoPreview((selectedPlayer as any).profile_photo);
      } else {
        setEditPhotoPreview(null);
      }
      setEditErrors({});
      setShowEditModal(true);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFormData({
      full_name: '',
      gender: '',
      father_name: '',
      mother_name: '',
      phone: '',
      whatsapp_number: '',
      email: '',
      dob: '',
      division: '',
      district: '',
      upazilla: '',
      playing_role: '',
      batting_style: '',
      bowling_style: '',
      jersey_size: '',
      nid: '',
      cricheroes_profile_link: '',
    });
    setEditProfilePhoto(null);
    setEditPhotoPreview(null);
    setEditErrors({});
  };
  
  const handleEditFieldChange = (field: string, value: string) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    if (editErrors[field]) {
      setEditErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEditDivisionChange = (value: string) => {
    // When division changes, check if current district is valid for new division
    const newDivisionDistricts = districts.filter((d) => d.division === value);
    const currentDistrictValid = newDivisionDistricts.some((d) => d.name === editFormData.district);
    
    setEditFormData((prev) => ({
      ...prev,
      division: value,
      // Only keep district if it's valid for the new division, otherwise clear it
      district: currentDistrictValid ? prev.district : '',
      upazilla: currentDistrictValid ? prev.upazilla : '',
    }));
  };

  const handleEditDistrictChange = (value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      district: value,
      upazilla: '',
    }));
  };

  const isValidURL = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateEditForm = () => {
    const newErrors: Record<string, string> = {};

    if (!editFormData.full_name.trim()) {
      newErrors.full_name = 'Full name is required.';
    }
    if (!editFormData.gender) {
      newErrors.gender = 'Gender is required.';
    }
    if (!editFormData.father_name.trim()) {
      newErrors.father_name = "Father's name is required.";
    }
    if (!editFormData.mother_name.trim()) {
      newErrors.mother_name = "Mother's name is required.";
    }
    if (!editFormData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    }
    if (!editFormData.whatsapp_number.trim()) {
      newErrors.whatsapp_number = 'WhatsApp number is required.';
    }
    if (editFormData.email && !isEmail(editFormData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!editFormData.dob) {
      newErrors.dob = 'Date of birth is required.';
    }
    if (!editFormData.division) {
      newErrors.division = 'Division is required.';
    }
    if (!editFormData.district) {
      newErrors.district = 'District is required.';
    }
    if (!editFormData.upazilla.trim()) {
      newErrors.upazilla = 'Upazilla is required.';
    }
    if (!editFormData.playing_role) {
      newErrors.playing_role = 'Playing role is required.';
    }
    if (!editFormData.batting_style) {
      newErrors.batting_style = 'Batting style is required.';
    }
    if (requiresBowlingStyle && !editFormData.bowling_style) {
      newErrors.bowling_style = 'Bowling style is required.';
    }
    if (editFormData.cricheroes_profile_link && !isValidURL(editFormData.cricheroes_profile_link)) {
      newErrors.cricheroes_profile_link = 'Please enter a valid URL.';
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      setEditProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setEditErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.profile_photo;
        return newErrors;
      });
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlayer) return;

    setEditErrors({});

    // Validate file if provided
    if (editProfilePhoto) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (editProfilePhoto.size > maxSize) {
        setEditErrors({ profile_photo: 'File size must be less than 5MB.' });
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(editProfilePhoto.type)) {
        setEditErrors({ profile_photo: 'Please upload a JPEG, PNG, GIF, or WEBP image.' });
        return;
      }
    }

    if (!validateEditForm()) {
      return;
    }

    setEditLoading(true);
    try {
      const formData = new FormData();
      
      formData.append('full_name', editFormData.full_name);
      formData.append('gender', editFormData.gender);
      formData.append('father_name', editFormData.father_name);
      formData.append('mother_name', editFormData.mother_name);
      formData.append('phone', editFormData.phone);
      formData.append('whatsapp_number', editFormData.whatsapp_number);
      if (editFormData.email) {
        formData.append('email', editFormData.email);
      }
      formData.append('dob', editFormData.dob);
      formData.append('division', editFormData.division);
      formData.append('district', editFormData.district);
      formData.append('upazilla', editFormData.upazilla);
      formData.append('playing_role', editFormData.playing_role);
      formData.append('batting_style', editFormData.batting_style);
      if (requiresBowlingStyle && editFormData.bowling_style) {
        formData.append('bowling_style', editFormData.bowling_style);
      }
      if (editFormData.jersey_size) {
        formData.append('jersey_size', editFormData.jersey_size);
      }
      if (editFormData.nid) {
        formData.append('nid', editFormData.nid);
      }
      if (editFormData.cricheroes_profile_link) {
        formData.append('cricheroes_profile_link', editFormData.cricheroes_profile_link);
      }
      if (editProfilePhoto) {
        formData.append('profile_photo', editProfilePhoto);
      }

      const response = await updatePlayerInfo(selectedPlayer.id, formData);
      const updatedPlayer = response.data;
      
      setPlayers(prev => prev.map(p => 
        p.id === updatedPlayer.id ? updatedPlayer : p
      ));
      setSelectedPlayer(updatedPlayer);
      handleCloseEditModal();
    } catch (err: any) {
      if (err.response?.status === 413) {
        setEditErrors({ profile_photo: 'File is too large. Maximum size is 5MB.' });
      } else {
        setEditErrors({ general: err.response?.data?.message || 'Failed to update player. Please try again.' });
      }
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-success';
      case 'DECLINED':
        return 'bg-danger';
      case 'PENDING_APPROVAL':
        return 'bg-warning text-dark';
      case 'PAYMENT_SUBMITTED':
        return 'bg-warning text-dark';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">Player Management</h1>
          <p className="text-muted-soft mb-0">Search, view, and manage all players</p>
        </div>
      </div>

      <div className="row g-4">
        <div className={selectedPlayer ? 'col-lg-7' : 'col-lg-12'}>
          <div className="card card-surface mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search players..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    autoFocus
                  />
                  <div className="form-text text-muted-soft">
                    Name, phone, email, transaction ID, or district
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="form-label mb-0">Status</label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleClearFilters}
                      disabled={isClearDisabled}
                    >
                      Clear Filters
                    </button>
                  </div>
                  <div className="d-flex flex-wrap gap-2 gap-md-3">
                    {['PAYMENT_SUBMITTED', 'PENDING_APPROVAL', 'APPROVED', 'DECLINED'].map((status) => (
                      <div className="form-check d-flex align-items-center gap-2" key={status}>
                        <input
                          className="form-check-input mt-0"
                          type="checkbox"
                          id={`status-${status}`}
                          checked={statusFilters.includes(status)}
                          onChange={() => toggleStatusFilter(status)}
                        />
                        <label className="form-check-label mb-0 small" htmlFor={`status-${status}`}>
                          {status.replace('_', ' ')}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-12 d-flex justify-content-end">
                  <button className="btn btn-arena w-100 w-md-auto" onClick={handleSearch}>
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-surface">
            <div className="card-body">
              {players.length === 0 ? (
                <div className="alert alert-secondary mb-0">No players found.</div>
              ) : (
                <>
                  <div className="small text-muted-soft mb-2">
                    Showing {players.length} of {players.length} results
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>District</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((player) => (
                          <tr key={player.id}>
                            <td className="fw-semibold">{player.name}</td>
                            <td>{player.phone}</td>
                            <td>{player.district_name || player.district_code || 'N/A'}</td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(player.status)} badge-status`}>
                                {player.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleViewPlayer(player.id)}
                              >
                                View
                              </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeletePlayer(player.id, player.name)}
                                  title="Delete Player"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="d-md-none">
                    {players.map((player) => (
                      <div key={player.id} className="card card-surface mb-3">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{player.name}</h6>
                              <div className="small text-muted-soft mb-1">
                                <i className="bi bi-telephone me-1"></i>
                                {player.phone}
                              </div>
                              <div className="small text-muted-soft mb-1">
                                <i className="bi bi-geo-alt me-1"></i>
                                {player.district_name || player.district_code || 'N/A'}
                              </div>
                            </div>
                            <span className={`badge ${getStatusBadgeClass(player.status)} badge-status`}>
                              {player.status.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="d-grid gap-2">
                          <button
                            type="button"
                              className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleViewPlayer(player.id)}
                          >
                            <i className="bi bi-eye me-1"></i>
                            View Details
                          </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeletePlayer(player.id, player.name)}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Delete Player
                          </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {selectedPlayer && (
          <div className="col-lg-5">
            <div className="card card-surface">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Player Details</h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedPlayer(null)}
                  >
                    Close
                  </button>
                </div>

                {/* Edit Player Button - Prominent */}
                <div className="mb-3 pb-3 border-bottom">
                  <button
                    type="button"
                    className="btn btn-arena w-100"
                    onClick={handleOpenEditModal}
                  >
                    <i className="bi bi-pencil me-2"></i>
                    Edit Player Info
                  </button>
                </div>

                <div className="mb-3">
                  <strong>Full Name:</strong> {selectedPlayer.name}
                </div>
                <div className="mb-3">
                  <strong>Phone:</strong> {selectedPlayer.phone}
                </div>
                {selectedPlayer.email && (
                  <div className="mb-3">
                    <strong>Email:</strong> {selectedPlayer.email}
                  </div>
                )}
                {selectedPlayer.dob && (
                  <div className="mb-3">
                    <strong>Date of Birth:</strong> {new Date(selectedPlayer.dob).toLocaleDateString()}
                  </div>
                )}
                {selectedPlayer.district_name && (
                  <div className="mb-3">
                    <strong>District:</strong> {selectedPlayer.district_name}
                  </div>
                )}
                {selectedPlayer.playing_role && (
                  <div className="mb-3">
                    <strong>Playing Role:</strong> {selectedPlayer.playing_role}
                  </div>
                )}
                {selectedPlayer.batting_style && (
                  <div className="mb-3">
                    <strong>Batting Style:</strong> {selectedPlayer.batting_style}
                  </div>
                )}
                {selectedPlayer.bowling_style && (
                  <div className="mb-3">
                    <strong>Bowling Style:</strong> {selectedPlayer.bowling_style}
                  </div>
                )}
                {selectedPlayer.jersey_size && (
                  <div className="mb-3">
                    <strong>Jersey Size:</strong> {selectedPlayer.jersey_size}
                  </div>
                )}
                {selectedPlayer.cricheroes_profile_link && (
                  <div className="mb-3">
                    <strong>Cricheroes Profile:</strong>{' '}
                    <a
                      href={selectedPlayer.cricheroes_profile_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-warning text-decoration-none"
                    >
                      View Profile <i className="bi bi-box-arrow-up-right"></i>
                    </a>
                  </div>
                )}
                {selectedPlayer.payment_method && (
                  <div className="mb-3">
                    <strong>Payment Method:</strong> {selectedPlayer.payment_method}
                  </div>
                )}
                {selectedPlayer.transaction_id && (
                  <div className="mb-3">
                    <strong>Transaction ID:</strong> <code className="small">{selectedPlayer.transaction_id}</code>
                  </div>
                )}
                <div className="mb-3">
                  <strong>Current Status:</strong>{' '}
                  <span className={`badge ${getStatusBadgeClass(selectedPlayer.status)} badge-status`}>
                    {selectedPlayer.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="border-top pt-3">
                  <strong className="d-block mb-2">Change Status:</strong>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => handleStatusChange(selectedPlayer.id, 'APPROVED')}
                      disabled={selectedPlayer.status === 'APPROVED'}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleStatusChange(selectedPlayer.id, 'DECLINED')}
                      disabled={selectedPlayer.status === 'DECLINED'}
                    >
                      Decline
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-warning"
                      onClick={() => handleStatusChange(selectedPlayer.id, 'PENDING_APPROVAL')}
                      disabled={selectedPlayer.status === 'PENDING_APPROVAL'}
                    >
                      Pending
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Player Modal */}
      {showEditModal && selectedPlayer && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseEditModal}
            style={{ zIndex: 1040 }}
          ></div>
          <div
            className="modal fade show"
            style={{ display: 'block', zIndex: 1050 }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content card-surface">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">Edit Player Info</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseEditModal}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {editErrors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {editErrors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors: Record<string, string> = { ...editErrors };
                            delete newErrors.general;
                            setEditErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="row g-3">
                      {/* Profile Photo */}
                      <div className="col-12">
                        <label className="form-label">Profile Photo</label>
                        <div className="d-flex flex-column flex-md-row align-items-start gap-3">
                          {editPhotoPreview && (
                            <div className="flex-shrink-0">
                              <img
                                src={editPhotoPreview}
                                alt="Preview"
                                className="rounded-circle"
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              />
                            </div>
                          )}
                          <div className="flex-grow-1">
                            <input
                              type="file"
                              className={`form-control ${editErrors.profile_photo ? 'is-invalid' : ''}`}
                              accept="image/*"
                              onChange={handleEditFileChange}
                              disabled={editLoading}
                            />
                            {editErrors.profile_photo && (
                              <div className="invalid-feedback">{editErrors.profile_photo}</div>
                            )}
                            <div className="form-text">Max 5MB. Formats: JPEG, PNG, GIF, WEBP</div>
                          </div>
                        </div>
                      </div>

                      {/* Full Name */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${editErrors.full_name ? 'is-invalid' : ''}`}
                          value={editFormData.full_name}
                          onChange={(e) => handleEditFieldChange('full_name', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.full_name && <div className="invalid-feedback">{editErrors.full_name}</div>}
                      </div>

                      {/* Gender */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Gender <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${editErrors.gender ? 'is-invalid' : ''}`}
                          value={editFormData.gender}
                          onChange={(e) => handleEditFieldChange('gender', e.target.value)}
                          disabled={editLoading}
                        >
                          <option value="">Select gender</option>
                          {genderOptions.map((gender) => (
                            <option key={gender} value={gender}>
                              {gender}
                            </option>
                          ))}
                        </select>
                        {editErrors.gender && <div className="invalid-feedback">{editErrors.gender}</div>}
                      </div>

                      {/* Father Name */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Father's Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${editErrors.father_name ? 'is-invalid' : ''}`}
                          value={editFormData.father_name}
                          onChange={(e) => handleEditFieldChange('father_name', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.father_name && <div className="invalid-feedback">{editErrors.father_name}</div>}
                      </div>

                      {/* Mother Name */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Mother's Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${editErrors.mother_name ? 'is-invalid' : ''}`}
                          value={editFormData.mother_name}
                          onChange={(e) => handleEditFieldChange('mother_name', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.mother_name && <div className="invalid-feedback">{editErrors.mother_name}</div>}
                      </div>

                      {/* Phone */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className={`form-control ${editErrors.phone ? 'is-invalid' : ''}`}
                          value={editFormData.phone}
                          onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.phone && <div className="invalid-feedback">{editErrors.phone}</div>}
                      </div>

                      {/* WhatsApp Number */}
                      <div className="col-md-6">
                        <label className="form-label">
                          WhatsApp Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className={`form-control ${editErrors.whatsapp_number ? 'is-invalid' : ''}`}
                          value={editFormData.whatsapp_number}
                          onChange={(e) => handleEditFieldChange('whatsapp_number', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.whatsapp_number && <div className="invalid-feedback">{editErrors.whatsapp_number}</div>}
                      </div>

                      {/* Email */}
                      <div className="col-md-6">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className={`form-control ${editErrors.email ? 'is-invalid' : ''}`}
                          value={editFormData.email}
                          onChange={(e) => handleEditFieldChange('email', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.email && <div className="invalid-feedback">{editErrors.email}</div>}
                      </div>

                      {/* Date of Birth */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Date of Birth <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className={`form-control ${editErrors.dob ? 'is-invalid' : ''}`}
                          value={editFormData.dob}
                          onChange={(e) => handleEditFieldChange('dob', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.dob && <div className="invalid-feedback">{editErrors.dob}</div>}
                      </div>

                      {/* Division */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Division <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${editErrors.division ? 'is-invalid' : ''}`}
                          value={editFormData.division}
                          onChange={(e) => handleEditDivisionChange(e.target.value)}
                          disabled={editLoading}
                        >
                          <option value="">Select division</option>
                          {divisions.map((division) => (
                            <option key={division} value={division}>
                              {division}
                            </option>
                          ))}
                        </select>
                        {editErrors.division && <div className="invalid-feedback">{editErrors.division}</div>}
                      </div>

                      {/* District */}
                      <div className="col-md-6">
                        <label className="form-label">
                          District <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${editErrors.district ? 'is-invalid' : ''}`}
                          value={editFormData.district}
                          onChange={(e) => handleEditDistrictChange(e.target.value)}
                          disabled={!editFormData.division || editLoading}
                        >
                          <option value="">
                            {editFormData.division ? 'Select district' : 'Select division first'}
                          </option>
                          {availableDistricts.map((district) => (
                            <option key={district.name} value={district.name}>
                              {district.name}
                            </option>
                          ))}
                          {/* Show current district if it exists but not in available list (for existing data) */}
                          {editFormData.district && editFormData.division && !availableDistricts.find(d => d.name === editFormData.district) && (
                            <option value={editFormData.district}>
                              {editFormData.district}
                            </option>
                          )}
                        </select>
                        {editErrors.district && <div className="invalid-feedback">{editErrors.district}</div>}
                      </div>

                      {/* Upazilla */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Upazilla <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${editErrors.upazilla ? 'is-invalid' : ''}`}
                          value={editFormData.upazilla}
                          onChange={(e) => handleEditFieldChange('upazilla', e.target.value)}
                          disabled={!editFormData.district || editLoading}
                        >
                          <option value="">
                            {editFormData.district ? 'Select upazilla' : 'Select district first'}
                          </option>
                          {availableUpazilas.map((upazila) => (
                            <option key={upazila.upazila} value={upazila.upazila}>
                              {upazila.upazila}
                            </option>
                          ))}
                          {/* Show current upazilla if it exists but not in available list (for existing data) */}
                          {editFormData.upazilla && editFormData.district && !availableUpazilas.find(u => u.upazila === editFormData.upazilla) && (
                            <option value={editFormData.upazilla}>
                              {editFormData.upazilla}
                            </option>
                          )}
                        </select>
                        {editErrors.upazilla && <div className="invalid-feedback">{editErrors.upazilla}</div>}
                      </div>

                      {/* Playing Role */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Playing Role <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${editErrors.playing_role ? 'is-invalid' : ''}`}
                          value={editFormData.playing_role}
                          onChange={(e) => handleEditFieldChange('playing_role', e.target.value)}
                          disabled={editLoading}
                        >
                          <option value="">Select role</option>
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        {editErrors.playing_role && <div className="invalid-feedback">{editErrors.playing_role}</div>}
                      </div>

                      {/* Batting Style */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Batting Style <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${editErrors.batting_style ? 'is-invalid' : ''}`}
                          value={editFormData.batting_style}
                          onChange={(e) => handleEditFieldChange('batting_style', e.target.value)}
                          disabled={editLoading}
                        >
                          <option value="">Select style</option>
                          {battingStyles.map((style) => (
                            <option key={style} value={style}>
                              {style}
                            </option>
                          ))}
                        </select>
                        {editErrors.batting_style && <div className="invalid-feedback">{editErrors.batting_style}</div>}
                      </div>

                      {/* Bowling Style */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Bowling Style {requiresBowlingStyle && <span className="text-danger">*</span>}
                        </label>
                        <select
                          className={`form-select ${editErrors.bowling_style ? 'is-invalid' : ''}`}
                          value={editFormData.bowling_style}
                          onChange={(e) => handleEditFieldChange('bowling_style', e.target.value)}
                          disabled={!requiresBowlingStyle || editLoading}
                        >
                          <option value="">
                            {requiresBowlingStyle ? 'Select style' : 'Select playing role first'}
                          </option>
                          {bowlingStyles.map((style) => (
                            <option key={style} value={style}>
                              {style}
                            </option>
                          ))}
                        </select>
                        {editErrors.bowling_style && <div className="invalid-feedback">{editErrors.bowling_style}</div>}
                      </div>

                      {/* Jersey Size */}
                      <div className="col-md-6">
                        <label className="form-label">Jersey Size</label>
                        <select
                          className="form-select"
                          value={editFormData.jersey_size}
                          onChange={(e) => handleEditFieldChange('jersey_size', e.target.value)}
                          disabled={editLoading}
                        >
                          <option value="">Select jersey size (optional)</option>
                          <option value="XS">XS</option>
                          <option value="S">S</option>
                          <option value="M">M</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                          <option value="XXXL">XXXL</option>
                        </select>
                      </div>

                      {/* NID */}
                      <div className="col-md-6">
                        <label className="form-label">NID (National ID)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFormData.nid}
                          onChange={(e) => handleEditFieldChange('nid', e.target.value)}
                          placeholder="Enter NID number"
                          disabled={editLoading}
                        />
                      </div>

                      {/* Cricheroes Profile Link */}
                      <div className="col-12">
                        <label className="form-label">Cricheroes Profile Link</label>
                        <input
                          type="url"
                          className={`form-control ${editErrors.cricheroes_profile_link ? 'is-invalid' : ''}`}
                          placeholder="https://cricheroes.in/..."
                          value={editFormData.cricheroes_profile_link}
                          onChange={(e) => handleEditFieldChange('cricheroes_profile_link', e.target.value)}
                          disabled={editLoading}
                        />
                        {editErrors.cricheroes_profile_link && (
                          <div className="invalid-feedback">{editErrors.cricheroes_profile_link}</div>
                        )}
                        <div className="form-text">Enter Cricheroes profile URL (optional)</div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCloseEditModal}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-arena"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Player Confirmation Modal */}
      <DeletePlayerModal
        show={deleteModal.show}
        playerName={deleteModal.playerName}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default AdminPlayersPage;

